import React, { useState, useEffect } from 'react';
import { 
  getAllArtMovements, 
  addArtMovement, 
  updateArtMovement, 
  deleteArtMovement 
} from '../services/artMovementService';

// Art movements array from ArtHistoryAppreciation.js to ensure exact matches
const artMovements = [
  'Abstract Art', 'Abstract Expressionism', 'Academic Art', 'Ancient', 'Art Deco', 'Art Informel', 'Art Nouveau',
  'Avant-garde', 'Baroque', 'Bronze Age', 'Conceptual Art', 'Contemporary', 'Contemporary Realism', 'Cubism',
  'Dada', 'Dutch Golden Age', 'Early Renaissance', 'Environmental Art (Land art)', 'Expressionism', 'Fauvism',
  'Feminist Art', 'Futurism', 'High Renaissance', 'Impressionism', 'Iron Age', 'Kinetic art', 'Kitsch',
  'Light and Space', 'Magic Realism', 'Medieval', 'Minimalism', 'Modernism', 'Naturalism', 'Neo-Dada',
  'Neo-Expressionism', 'Neo-Impressionism', 'Neo-Minimalism (Neo-Geo)', 'Neo-Pop Art', 'Neo-Suprematism',
  'Neoclassicism', 'Neolithic', 'Op Art', 'Orientalism', 'Paleolithic', 'Photorealism (Super-Realism, Hyper-Realism)',
  'Pointillism', 'Pop Art', 'Post-Impressionism', 'Post-Minimalism', 'Post-Painterly Abstraction', 'Postmodern',
  'Realism', 'Renaissance', 'Rococo', 'Romanticism', 'Social Realism', 'Socialist Realism', 'Street art',
  'Surrealism', 'Symbolism', 'Tachisme'
];

const ArtMovementAdmin = () => {
  const [artMovementsData, setArtMovementsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    characteristics: [],
    timePeriod: '',
    famousArtists: [],
    techniques: []
  });
  const [newCharacteristic, setNewCharacteristic] = useState('');
  const [newArtist, setNewArtist] = useState('');
  const [newTechnique, setNewTechnique] = useState('');

  useEffect(() => {
    loadArtMovements();
  }, []);

  const loadArtMovements = async () => {
    try {
      setLoading(true);
      const data = await getAllArtMovements();
      setArtMovementsData(data);
    } catch (err) {
      setError('Failed to load art movements');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Check if we're editing an existing document
        // If the editingId is not the same as the form name, we need to delete the old one and create a new one
        if (editingId !== formData.name) {
          // Delete the old document (could be random ID or old name)
          await deleteArtMovement(editingId);
          // Create new document with correct ID (name)
          await addArtMovement(formData);
        } else {
          // Try to update existing document with correct ID
          try {
            await updateArtMovement(editingId, formData);
          } catch (updateError) {
            // If update fails (document doesn't exist), create new one
            console.log('Update failed, creating new document:', updateError);
            await addArtMovement(formData);
          }
        }
      } else {
        await addArtMovement(formData);
      }
      
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadArtMovements();
    } catch (err) {
      setError('Failed to save art movement');
      console.error(err);
    }
  };

  const handleEdit = (artMovement) => {
    setFormData({
      name: artMovement.name || '',
      description: artMovement.description || '',
      characteristics: artMovement.characteristics || [],
      timePeriod: artMovement.timePeriod || '',
      famousArtists: artMovement.famousArtists || [],
      techniques: artMovement.techniques || []
    });
    setEditingId(artMovement.id); // Use original id for editing
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this art movement?')) {
      try {
        await deleteArtMovement(id);
        loadArtMovements();
      } catch (err) {
        setError('Failed to delete art movement');
        console.error(err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      characteristics: [],
      timePeriod: '',
      famousArtists: [],
      techniques: []
    });
    setNewCharacteristic('');
    setNewArtist('');
    setNewTechnique('');
  };

  const addCharacteristic = () => {
    if (newCharacteristic.trim()) {
      setFormData(prev => ({
        ...prev,
        characteristics: [...prev.characteristics, newCharacteristic.trim()]
      }));
      setNewCharacteristic('');
    }
  };

  const removeCharacteristic = (index) => {
    setFormData(prev => ({
      ...prev,
      characteristics: prev.characteristics.filter((_, i) => i !== index)
    }));
  };

  const addArtist = () => {
    if (newArtist.trim()) {
      setFormData(prev => ({
        ...prev,
        famousArtists: [...prev.famousArtists, newArtist.trim()]
      }));
      setNewArtist('');
    }
  };

  const removeArtist = (index) => {
    setFormData(prev => ({
      ...prev,
      famousArtists: prev.famousArtists.filter((_, i) => i !== index)
    }));
  };

  const addTechnique = () => {
    if (newTechnique.trim()) {
      setFormData(prev => ({
        ...prev,
        techniques: [...prev.techniques, newTechnique.trim()]
      }));
      setNewTechnique('');
    }
  };

  const removeTechnique = (index) => {
    setFormData(prev => ({
      ...prev,
      techniques: prev.techniques.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-4 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-amber-800">Art Movement Admin Panel</h1>
              <p className="text-amber-600 mt-1">Manage art movement descriptions and information</p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setEditingId(null);
                setShowForm(true);
              }}
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors duration-200 shadow-lg"
            >
              Add New Art Movement
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
            <p className="font-semibold">Error</p>
            <p>{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-red-500 hover:text-red-700 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl border-4 border-amber-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-amber-800">
                    {editingId ? 'Edit Art Movement' : 'Add New Art Movement'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                      resetForm();
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Art Movement Name *
                    </label>
                    <select
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-lg"
                      required
                    >
                      <option value="">Select an art movement...</option>
                      {artMovements.map(movement => (
                        <option key={movement} value={movement}>{movement}</option>
                      ))}
                    </select>
                    <p className="text-sm text-gray-500 mt-1">
                      Select from the exact list used in artwork uploads to ensure perfect matching
                    </p>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 h-32 resize-none"
                      placeholder="Detailed description of the art movement..."
                      required
                    />
                  </div>

                  {/* Time Period */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Time Period
                    </label>
                    <input
                      type="text"
                      value={formData.timePeriod}
                      onChange={(e) => setFormData(prev => ({ ...prev, timePeriod: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3"
                      placeholder="e.g., 1880s - 1900s"
                    />
                  </div>

                  {/* Characteristics */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Key Characteristics
                    </label>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        value={newCharacteristic}
                        onChange={(e) => setNewCharacteristic(e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2"
                        placeholder="Add a characteristic..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCharacteristic())}
                      />
                      <button
                        type="button"
                        onClick={addCharacteristic}
                        className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.characteristics.map((char, index) => (
                        <span
                          key={index}
                          className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                        >
                          {char}
                          <button
                            type="button"
                            onClick={() => removeCharacteristic(index)}
                            className="text-amber-600 hover:text-amber-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Famous Artists */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Famous Artists
                    </label>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        value={newArtist}
                        onChange={(e) => setNewArtist(e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2"
                        placeholder="Add an artist name..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArtist())}
                      />
                      <button
                        type="button"
                        onClick={addArtist}
                        className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.famousArtists.map((artist, index) => (
                        <span
                          key={index}
                          className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                        >
                          {artist}
                          <button
                            type="button"
                            onClick={() => removeArtist(index)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Techniques */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Common Techniques
                    </label>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        value={newTechnique}
                        onChange={(e) => setNewTechnique(e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-4 py-2"
                        placeholder="Add a technique..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTechnique())}
                      />
                      <button
                        type="button"
                        onClick={addTechnique}
                        className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.techniques.map((technique, index) => (
                        <span
                          key={index}
                          className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                        >
                          {technique}
                          <button
                            type="button"
                            onClick={() => removeTechnique(index)}
                            className="text-green-600 hover:text-green-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex gap-4 pt-4">
                    <button
                      type="submit"
                      className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 rounded-xl font-semibold transition-colors duration-200"
                    >
                      {editingId ? 'Update Art Movement' : 'Add Art Movement'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false);
                        setEditingId(null);
                        resetForm();
                      }}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-8 py-3 rounded-xl font-semibold transition-colors duration-200"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Art Movements List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {artMovementsData.map((movement) => (
            <div key={movement.id} className="bg-white rounded-2xl shadow-lg p-6 border-4 border-amber-200">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-bold text-amber-800">{movement.name}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(movement)}
                    className="text-blue-500 hover:text-blue-700 p-1"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(movement.id)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <p className="text-gray-700 text-sm mb-3 line-clamp-3">
                {movement.description}
              </p>
              
              {movement.timePeriod && (
                <p className="text-amber-600 text-sm font-medium mb-2">
                  {movement.timePeriod}
                </p>
              )}
              
              <div className="space-y-2">
                {movement.characteristics && movement.characteristics.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Characteristics:</p>
                    <div className="flex flex-wrap gap-1">
                      {movement.characteristics.slice(0, 3).map((char, index) => (
                        <span key={index} className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs">
                          {char}
                        </span>
                      ))}
                      {movement.characteristics.length > 3 && (
                        <span className="text-gray-500 text-xs">+{movement.characteristics.length - 3} more</span>
                      )}
                    </div>
                  </div>
                )}
                
                {movement.famousArtists && movement.famousArtists.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-1">Artists:</p>
                    <p className="text-xs text-gray-700">
                      {movement.famousArtists.slice(0, 2).join(', ')}
                      {movement.famousArtists.length > 2 && ` +${movement.famousArtists.length - 2} more`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {artMovementsData.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No art movements found. Add your first one!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtMovementAdmin;
